import React from "react";
import { useHistory, useLocation } from "react-router";

import { Avatar, Button } from "~/ui";

import { PARAM_NAME as ATTENDEE_PARAM_NAME } from "../Filters/AttendeeFilter";
import { useLibraryQueryParams } from "../Filters/Filters";
import { ShowMoreToggle } from "./CompanyPanel";

import styles from "./CompanyPanel.module.css";

const MAX_EMAILS_SHOWN_INITIAL = 5;
const EMAILS_SHOWN_INCREMENT = 5;

function MetWithExternalUser({ email, isActive }: { email: string; isActive: boolean }) {
	const history = useHistory();
	const location = useLocation();

	const setAttendeeFilter = React.useCallback(() => {
		const search = new URLSearchParams(location.search);
		search.set(ATTENDEE_PARAM_NAME, email);
		history.replace({
			pathname: location.pathname,
			search: search.toString(),
		});
	}, [location.search, location.pathname, email, history]);

	return (
		<div className={styles.metWithUser}>
			<div className={styles.metWithUserEmail}>
				<Avatar user={{ fullName: email }} size="small" />
				<p title={email}>{email}</p>
			</div>
			<Button className={styles.viewMeetingsButton} onClick={setAttendeeFilter} size="slim" disabled={isActive}>
				View meetings
			</Button>
		</div>
	);
}

export default function MetWithEmails({ companyEmails }: { companyEmails: string[] }) {
	const [numEmailsShown, setNumEmailsShown] = React.useState(MAX_EMAILS_SHOWN_INITIAL);
	const onShowMoreEmails = React.useCallback(() => {
		setNumEmailsShown(prev => {
			if (prev >= companyEmails.length) {
				return MAX_EMAILS_SHOWN_INITIAL;
			} else {
				return prev + EMAILS_SHOWN_INCREMENT;
			}
		});
	}, [companyEmails.length]);

	const areEmailsOverflowing = companyEmails.length > MAX_EMAILS_SHOWN_INITIAL;
	const areEmailsExpanded = numEmailsShown >= companyEmails.length;
	const emailsToShow = React.useMemo(() => companyEmails.slice(0, numEmailsShown), [companyEmails, numEmailsShown]);

	const { attendeeEmails } = useLibraryQueryParams();
	const activeAttendeeEmails = attendeeEmails?.value ?? [];

	return (
		<>
			{emailsToShow.map(email => (
				<MetWithExternalUser key={email} email={email} isActive={activeAttendeeEmails.includes(email)} />
			))}
			{areEmailsOverflowing && <ShowMoreToggle isExpanded={areEmailsExpanded} onClick={onShowMoreEmails} />}
		</>
	);
}
